import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Play, MessageSquareText, ListTree, ClipboardList, Users, Layers, FileSearch, UserPlus, Plus, Trash2, ArrowUp, ArrowDown,
  RotateCcw, Send, Undo2, CornerDownRight, AlertTriangle, MessageCircle, Square,
} from 'lucide-react';
import { useOrganizacao } from '../organizacao';
import { formatWa } from '../waFormat';

/* Construtor visual do assistente: o fluxo inteiro desenhado como uma árvore da esquerda para a direita
 * (cliente escreve → saudação → menu → o que cada opção faz), com edição ao lado e um simulador para testar
 * antes de salvar. O motor que executa este fluxo está em backend/routes/whatsapp_fluxo.py. */

export const FLUXO_PADRAO = {
  saudacao: 'Olá, {nome}! 👋 Aqui é a *{assistente}*.',
  menus: {
    inicio: {
      id: 'inicio', titulo: 'Menu principal', texto: 'Como posso ajudar? Responda com o *número* da opção:',
      opcoes: [
        { id: 'op_projeto', rotulo: 'Consultar o andamento do meu projeto', acao: 'consulta_projeto', visivel: 'todos' },
        { id: 'op_atendente', rotulo: 'Falar com um atendente', acao: 'atendente', visivel: 'todos' },
        { id: 'op_cadastro', rotulo: 'Fazer meu cadastro', acao: 'cadastro', visivel: 'nao_clientes' },
      ],
    },
  },
  coletas: {},
};

const ACOES = [
  ['atendente', 'Falar com atendente (fila)', Users],
  ['setor', 'Ir para a fila de um setor', Layers],
  ['submenu', 'Abrir um submenu', ListTree],
  ['mensagem', 'Responder com uma mensagem', MessageSquareText],
  ['coleta', 'Coletar dados do cliente', ClipboardList],
  ['consulta_projeto', 'Consultar projeto pelo CPF', FileSearch],
  ['cadastro', 'Fazer o cadastro do cliente', UserPlus],
];
const TIPOS = [['texto', 'Texto livre'], ['numero', 'Número'], ['email', 'E-mail'], ['telefone', 'Telefone'], ['cpf', 'CPF ou CNPJ'], ['data', 'Data']];
const novoId = (p) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const clone = (o) => JSON.parse(JSON.stringify(o));
const norm = (t = '') => t.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/* ---------- mesmas regras de texto do backend (para desenhar e simular) ---------- */
const SAUDACAO_ANTIGA = 'Olá, {nome}! 👋 Sou o assistente virtual da *{empresa}*.';
export function saudacaoTexto(fluxo, nome, empresa, assistente = '') {
  let s = fluxo?.saudacao || FLUXO_PADRAO.saudacao;
  if (s.trim() === SAUDACAO_ANTIGA) s = FLUXO_PADRAO.saudacao;   // padrão antigo nunca editado (igual ao backend)
  if (!assistente) s = s.replace(/\s*Aqui é a \*?\{assistente\}\*?\.?/g, '');
  s = s.replaceAll('{assistente}', assistente);
  if (!empresa) s = s.replace(/\s*\b(d[aeo]s?|na|no|em)\s*\*?\{empresa\}\*?/g, '');
  if (!nome) s = s.replace(/,?\s*\*?\{nome\}\*?/g, '');
  return s.replaceAll('{nome}', nome).replaceAll('{empresa}', empresa).replace(/\s+([!.,?])/g, '$1').trim();
}
const visiveis = (menu, cliente) => (menu?.opcoes || []).filter(o => !((o.visivel === 'clientes' && !cliente) || (o.visivel === 'nao_clientes' && cliente) || (o.acao === 'cadastro' && cliente)));
export function textoMenu(fluxo, id, cliente) {
  const menu = fluxo?.menus?.[id] || fluxo?.menus?.inicio || FLUXO_PADRAO.menus.inicio;
  const linhas = visiveis(menu, cliente).map((o, i) => `*${i + 1}* – ${o.rotulo}`);
  if (menu.id !== 'inicio') linhas.push('*0* – Voltar ao menu principal');
  return `${menu.texto || 'Escolha uma opção:'}\n\n${linhas.join('\n')}`;
}

/* ---------- problemas que impedem salvar (o backend valida de novo) ---------- */
function problemas(fluxo, setores) {
  const out = [];
  const ids = new Set(setores.map(s => s.id));
  Object.values(fluxo.menus || {}).forEach(m => {
    if (!m.opcoes?.length) out.push(`“${m.titulo}” não tem opções`);
    if ((m.opcoes || []).length > 9) out.push(`“${m.titulo}” tem mais de 9 opções`);
    (m.opcoes || []).forEach(o => {
      if (!o.rotulo?.trim()) out.push(`Há uma opção sem texto em “${m.titulo}”`);
      if (o.acao === 'setor' && !ids.has(o.setor_id)) out.push(`Escolha o setor de “${o.rotulo || 'opção'}”`);
      if (o.acao === 'submenu' && !fluxo.menus[o.menu_id]) out.push(`“${o.rotulo || 'opção'}” aponta para um submenu que não existe`);
      if (o.acao === 'coleta' && !fluxo.coletas?.[o.coleta_id]) out.push(`“${o.rotulo || 'opção'}” aponta para uma coleta que não existe`);
      if (o.acao === 'mensagem' && !o.texto?.trim()) out.push(`Escreva a mensagem de “${o.rotulo || 'opção'}”`);
    });
  });
  Object.values(fluxo.coletas || {}).forEach(c => {
    if (!(c.perguntas || []).some(p => p.pergunta?.trim())) out.push(`A coleta “${c.titulo}” não tem perguntas`);
    if (c.destino?.acao === 'setor' && !ids.has(c.destino.setor_id)) out.push(`Escolha o setor de destino da coleta “${c.titulo}”`);
  });
  return out;
}

export default function FluxoConfig({ cfg, up, admin }) {
  const { setores } = useOrganizacao();
  const fluxo = cfg.fluxo || FLUXO_PADRAO;
  const [sel, setSel] = useState({ tipo: 'menu', id: 'inicio' });
  const [painel, setPainel] = useState('editar');
  const setFluxo = (fn) => { const n = clone(fluxo); fn(n); up('fluxo')(n); };
  const avisos = useMemo(() => problemas(fluxo, setores), [fluxo, setores]);
  const setorDe = (id) => setores.find(s => s.id === id);

  return (
    <div className="grid xl:grid-cols-[1fr_400px] gap-4 items-start" data-testid="wa-config-fluxo">
      <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 min-h-[520px] overflow-auto">
        <div className="sticky left-0 top-0 z-10 px-4 py-3 flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur">
          <h3 className="font-semibold text-sm">Fluxo do assistente</h3>
          <span className="text-xs text-slate-500">Clique em uma caixa para editar. O cliente responde com o número ou com o texto da opção.</span>
          <span className="flex-1" />
          {admin && (
            <button onClick={() => { if (window.confirm('Voltar ao fluxo padrão? As mudanças não salvas serão perdidas.')) { up('fluxo')(clone(FLUXO_PADRAO)); setSel({ tipo: 'menu', id: 'inicio' }); } }}
              className="h-8 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs flex items-center gap-1.5 hover:bg-white dark:hover:bg-slate-800" data-testid="wa-fluxo-padrao">
              <RotateCcw className="w-3.5 h-3.5" /> Fluxo padrão
            </button>
          )}
        </div>
        {avisos.length > 0 && (
          <div className="mx-4 mt-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-xs text-amber-900 dark:text-amber-200" data-testid="wa-fluxo-avisos">
            <p className="font-semibold flex items-center gap-1.5 mb-0.5"><AlertTriangle className="w-3.5 h-3.5" /> Corrija antes de salvar</p>
            <ul className="list-disc pl-5">{avisos.slice(0, 5).map(a => <li key={a}>{a}</li>)}</ul>
          </div>
        )}
        <div className="p-5 w-max min-w-full">
          <div className="flex items-start gap-0">
            <Inicio />
            <Conector />
            <Caixa cor="slate" icone={MessageCircle} titulo="Saudação" ativo={sel.tipo === 'saudacao'} onClick={() => { setSel({ tipo: 'saudacao' }); setPainel('editar'); }} testid="wa-no-saudacao">
              <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap line-clamp-3">{formatWa(saudacaoTexto(fluxo, 'João', cfg.nome_empresa || '', cfg.bot_nome || ''))}</p>
            </Caixa>
            <Conector />
            <NoMenu fluxo={fluxo} id="inicio" sel={sel} setSel={(s) => { setSel(s); setPainel('editar'); }} setorDe={setorDe} visitados={[]} />
          </div>
        </div>
      </section>

      <aside className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 xl:sticky xl:top-16 overflow-hidden">
        <div className="flex border-b border-slate-200 dark:border-slate-800 text-sm">
          {[['editar', 'Editar'], ['testar', 'Testar conversa']].map(([k, l]) => (
            <button key={k} onClick={() => setPainel(k)} data-testid={`wa-fluxo-painel-${k}`}
              className={`flex-1 h-11 font-medium border-b-2 -mb-px ${painel === k ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400' : 'border-transparent text-slate-500'}`}>{l}</button>
          ))}
        </div>
        {painel === 'testar'
          ? <Simulador fluxo={fluxo} empresa={cfg.nome_empresa || ''} assistente={cfg.bot_nome || ''} setorDe={setorDe} />
          : (
            <fieldset disabled={!admin} className="p-4 max-h-[calc(100dvh-14rem)] overflow-y-auto">
              {sel.tipo === 'saudacao' && <EditorSaudacao fluxo={fluxo} setFluxo={setFluxo} empresa={cfg.nome_empresa} assistente={cfg.bot_nome} />}
              {sel.tipo === 'menu' && fluxo.menus[sel.id] && (
                <EditorMenu fluxo={fluxo} setFluxo={setFluxo} id={sel.id} foco={sel.op} setores={setores} setSel={setSel} />
              )}
              {sel.tipo === 'coleta' && fluxo.coletas?.[sel.id] && (
                <EditorColeta fluxo={fluxo} setFluxo={setFluxo} id={sel.id} setores={setores} setSel={setSel} />
              )}
              {!admin && <p className="text-xs text-amber-700 mt-3">Somente administradores alteram o fluxo.</p>}
            </fieldset>
          )}
      </aside>
    </div>
  );
}

/* ============================== desenho ============================== */

const CORES = {
  emerald: 'border-emerald-300 dark:border-emerald-800', violet: 'border-violet-300 dark:border-violet-800',
  sky: 'border-sky-300 dark:border-sky-800', slate: 'border-slate-300 dark:border-slate-700', amber: 'border-amber-300 dark:border-amber-800',
};
const TOPO = { emerald: 'bg-emerald-600', violet: 'bg-violet-600', sky: 'bg-sky-600', slate: 'bg-slate-500', amber: 'bg-amber-500' };

function Inicio() {
  return (
    <div className="mt-3 h-10 px-3 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-semibold flex items-center gap-1.5 shrink-0">
      <Play className="w-3.5 h-3.5" /> Cliente escreve
    </div>
  );
}
const Conector = () => <div className="mt-8 w-8 h-px bg-slate-300 dark:bg-slate-700 shrink-0" />;

function Caixa({ cor, icone: Icone, titulo, ativo, onClick, children, testid, largura = 'w-64' }) {
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick?.()} data-testid={testid}
      className={`${largura} shrink-0 rounded-xl border-2 bg-white dark:bg-slate-900 overflow-hidden cursor-pointer transition-shadow ${CORES[cor]} ${ativo ? 'ring-4 ring-emerald-500/30 shadow-lg' : 'hover:shadow-md'}`}>
      <div className={`${TOPO[cor]} text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5`}><Icone className="w-3.5 h-3.5" />{titulo}</div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Fim({ children, cor, icone: Icone = Square, testid }) {
  return (
    <div className="h-9 px-3 rounded-full border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs flex items-center gap-1.5 shrink-0 mt-1"
      style={cor ? { borderColor: cor, color: cor } : undefined} data-testid={testid}>
      <Icone className="w-3.5 h-3.5" />{children}
    </div>
  );
}

/* um menu e, à direita, para onde cada opção leva */
function NoMenu({ fluxo, id, sel, setSel, setorDe, visitados }) {
  const menu = fluxo.menus[id];
  if (!menu) return <Fim>submenu removido</Fim>;
  const ativo = sel.tipo === 'menu' && sel.id === id;
  return (
    <div className="flex items-start">
      <Caixa cor="emerald" icone={ListTree} titulo={menu.titulo || (id === 'inicio' ? 'Menu principal' : 'Submenu')} ativo={ativo}
        onClick={() => setSel({ tipo: 'menu', id })} testid={`wa-no-menu-${id}`} largura="w-72">
        <p className="text-xs text-slate-500 mb-2 line-clamp-2">{menu.texto}</p>
        <ol className="space-y-1">
          {(menu.opcoes || []).map((o, i) => (
            <li key={o.id} onClick={(e) => { e.stopPropagation(); setSel({ tipo: 'menu', id, op: o.id }); }}
              className={`flex items-center gap-2 text-[13px] rounded-md px-1.5 py-1 hover:bg-slate-50 dark:hover:bg-slate-800 ${sel.op === o.id && ativo ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''}`}>
              <span className="w-5 h-5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="truncate flex-1">{o.rotulo || <i className="text-slate-400">sem texto</i>}</span>
              {o.visivel !== 'todos' && o.visivel && <span className="text-[10px] text-slate-400 shrink-0">{o.visivel === 'clientes' ? 'só clientes' : 'não clientes'}</span>}
            </li>
          ))}
          {id !== 'inicio' && <li className="flex items-center gap-2 text-[12px] text-slate-400 px-1.5"><span className="w-5 text-center font-bold">0</span>Voltar ao principal</li>}
        </ol>
      </Caixa>
      <div className="flex flex-col gap-3">
        {(menu.opcoes || []).map((o, i, arr) => {
          const primeiro = i === 0; const ultimo = i === arr.length - 1;
          return (
            <div key={o.id} className="relative flex items-start pl-10">
              {/* traço vertical que liga as opções (cada filho desenha o seu pedaço) */}
              {arr.length > 1 && (
                <span className="absolute left-0 w-px bg-slate-300 dark:bg-slate-700"
                  style={{ top: primeiro ? 22 : -12, bottom: ultimo ? 'auto' : -12, height: ultimo ? 34 : undefined }} />
              )}
              <span className="absolute left-0 top-[22px] w-10 h-px bg-slate-300 dark:bg-slate-700" />
              <span className="absolute left-3 top-[13px] text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-slate-50 dark:bg-slate-950 px-0.5 leading-[18px]">{i + 1}</span>
              <Destino fluxo={fluxo} o={o} sel={sel} setSel={setSel} setorDe={setorDe} visitados={[...visitados, id]} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Destino({ fluxo, o, sel, setSel, setorDe, visitados }) {
  if (o.acao === 'submenu') {
    if (visitados.includes(o.menu_id)) return <Fim icone={Undo2}>volta para “{fluxo.menus[o.menu_id]?.titulo}”</Fim>;
    return <NoMenu fluxo={fluxo} id={o.menu_id} sel={sel} setSel={setSel} setorDe={setorDe} visitados={visitados} />;
  }
  if (o.acao === 'coleta') {
    const c = fluxo.coletas?.[o.coleta_id];
    if (!c) return <Fim>coleta removida</Fim>;
    const s = c.destino?.acao === 'setor' ? setorDe(c.destino.setor_id) : null;
    return (
      <div className="flex items-start">
        <Caixa cor="violet" icone={ClipboardList} titulo={c.titulo || 'Coleta de dados'} ativo={sel.tipo === 'coleta' && sel.id === c.id}
          onClick={() => setSel({ tipo: 'coleta', id: c.id })} testid={`wa-no-coleta-${c.id}`}>
          <ol className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5 list-decimal pl-4">
            {(c.perguntas || []).map((p, i) => <li key={i} className="truncate">{p.pergunta || <i className="text-slate-400">pergunta vazia</i>}</li>)}
          </ol>
        </Caixa>
        <Conector />
        {c.destino?.acao === 'setor' ? <Fim cor={s?.cor} icone={Layers}>fila de {s?.nome || '?'}</Fim>
          : c.destino?.acao === 'menu' ? <Fim icone={Undo2}>volta ao menu</Fim>
          : c.destino?.acao === 'fim' ? <Fim>encerra o assistente</Fim>
          : <Fim icone={Users}>fila de atendimento</Fim>}
      </div>
    );
  }
  if (o.acao === 'mensagem') {
    return (
      <div className="flex items-start">
        <Caixa cor="sky" icone={MessageSquareText} titulo="Mensagem" ativo={false} onClick={() => setSel({ tipo: 'menu', id: visitados[visitados.length - 1], op: o.id })} largura="w-56">
          <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-3 whitespace-pre-wrap">{o.texto || <i className="text-slate-400">sem texto</i>}</p>
        </Caixa>
        <Conector />
        {o.depois === 'atendente' ? <Fim icone={Users}>fila de atendimento</Fim> : o.depois === 'fim' ? <Fim>aguarda o cliente</Fim> : <Fim icone={Undo2}>mostra o menu de novo</Fim>}
      </div>
    );
  }
  if (o.acao === 'setor') { const s = setorDe(o.setor_id); return <Fim cor={s?.cor} icone={Layers} testid={`wa-fim-setor-${o.id}`}>fila de {s?.nome || 'setor ?'}</Fim>; }
  if (o.acao === 'atendente') return <Fim icone={Users}>fila de atendimento (pergunta o setor se houver)</Fim>;
  if (o.acao === 'consulta_projeto') return <Fim icone={FileSearch} cor="#b45309">consulta pelo CPF e mostra a etapa do projeto</Fim>;
  if (o.acao === 'cadastro') return <Fim icone={UserPlus} cor="#b45309">cadastro em 5 perguntas, validado por um atendente</Fim>;
  return <Fim>?</Fim>;
}

/* ============================== editores ============================== */

const inputCls = 'mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30 font-normal';
const areaCls = 'mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30 font-normal';

function EditorSaudacao({ fluxo, setFluxo, empresa, assistente }) {
  return (
    <div className="space-y-3" data-testid="wa-ed-saudacao">
      <h4 className="font-semibold text-sm">Saudação</h4>
      <label className="block text-sm font-medium">Primeira frase do assistente
        <textarea rows={3} value={fluxo.saudacao || ''} onChange={e => setFluxo(f => { f.saudacao = e.target.value; })} className={areaCls} data-testid="wa-ed-saudacao-texto" />
      </label>
      <p className="text-xs text-slate-500">
        <code>{'{nome}'}</code> = primeiro nome do cliente (some se não souber), <code>{'{empresa}'}</code> = nome da empresa
        {empresa ? <> (“{empresa}”)</> : <> (defina em Assistente)</>}, <code>{'{assistente}'}</code> = nome do assistente
        {assistente ? <> (“{assistente}”)</> : null}. Logo depois vem o menu principal.
      </p>
    </div>
  );
}

function EditorMenu({ fluxo, setFluxo, id, foco, setores, setSel }) {
  const menu = fluxo.menus[id];
  const refOp = useRef({});
  useEffect(() => { if (foco) refOp.current[foco]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [foco]);
  const muda = (fn) => setFluxo(f => fn(f.menus[id], f));
  const usado = Object.values(fluxo.menus).some(m => m.opcoes?.some(o => o.acao === 'submenu' && o.menu_id === id));
  const novoSubmenu = (op) => {
    const nid = novoId('m');
    setFluxo(f => {
      f.menus[nid] = { id: nid, titulo: op.rotulo || 'Submenu', texto: 'Escolha uma opção:', opcoes: [{ id: novoId('op'), rotulo: 'Falar com um atendente', acao: 'atendente', visivel: 'todos' }] };
      const alvo = f.menus[id].opcoes.find(x => x.id === op.id); alvo.menu_id = nid;
    });
  };
  const novaColeta = (op) => {
    const nid = novoId('c');
    setFluxo(f => {
      f.coletas = f.coletas || {};
      f.coletas[nid] = { id: nid, titulo: op.rotulo || 'Coleta de dados', intro: 'Vou anotar alguns dados para agilizar seu atendimento.', perguntas: [{ campo: '', pergunta: 'Qual o seu nome completo?', tipo: 'texto' }], final_texto: 'Obrigado! Já vou te passar para um atendente.', destino: { acao: 'fila' } };
      const alvo = f.menus[id].opcoes.find(x => x.id === op.id); alvo.coleta_id = nid;
    });
  };
  return (
    <div className="space-y-3" data-testid={`wa-ed-menu-${id}`}>
      <div className="flex items-center gap-2">
        <h4 className="font-semibold text-sm flex-1">{id === 'inicio' ? 'Menu principal' : 'Submenu'}</h4>
        {id !== 'inicio' && (
          <button type="button" disabled={usado} title={usado ? 'Tire as opções que abrem este submenu antes' : 'Excluir submenu'}
            onClick={() => { setFluxo(f => { delete f.menus[id]; }); setSel({ tipo: 'menu', id: 'inicio' }); }}
            className="h-7 px-2 rounded-md text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-40 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
        )}
      </div>
      {id !== 'inicio' && (
        <label className="block text-sm font-medium">Nome (só para você)
          <input value={menu.titulo || ''} onChange={e => muda(m => { m.titulo = e.target.value; })} className={inputCls} />
        </label>
      )}
      <label className="block text-sm font-medium">Texto antes das opções
        <textarea rows={2} value={menu.texto || ''} onChange={e => muda(m => { m.texto = e.target.value; })} className={areaCls} data-testid="wa-ed-menu-texto" />
      </label>
      <div className="space-y-2">
        <p className="text-sm font-medium">Opções</p>
        {(menu.opcoes || []).map((o, i) => (
          <div key={o.id} ref={el => { refOp.current[o.id] = el; }} data-testid={`wa-ed-op-${i}`}
            className={`rounded-lg border p-2.5 space-y-2 ${foco === o.id ? 'border-emerald-400 ring-2 ring-emerald-500/20' : 'border-slate-200 dark:border-slate-700'}`}>
            <div className="flex items-center gap-1.5">
              <span className="w-6 h-6 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <input value={o.rotulo} onChange={e => muda(m => { m.opcoes[i].rotulo = e.target.value; })} placeholder="Texto da opção"
                className="flex-1 min-w-0 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" data-testid={`wa-ed-op-rotulo-${i}`} />
              <button type="button" disabled={i === 0} onClick={() => muda(m => { [m.opcoes[i - 1], m.opcoes[i]] = [m.opcoes[i], m.opcoes[i - 1]]; })} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" aria-label="Subir"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" disabled={i === menu.opcoes.length - 1} onClick={() => muda(m => { [m.opcoes[i + 1], m.opcoes[i]] = [m.opcoes[i], m.opcoes[i + 1]]; })} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" aria-label="Descer"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" disabled={menu.opcoes.length <= 1} onClick={() => muda(m => { m.opcoes.splice(i, 1); })} className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-30" aria-label="Remover"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <select value={o.acao} onChange={e => muda(m => { m.opcoes[i] = { id: o.id, rotulo: o.rotulo, visivel: o.visivel, acao: e.target.value, ...(e.target.value === 'mensagem' ? { texto: '', depois: 'menu' } : {}) }; })}
              className="w-full h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" data-testid={`wa-ed-op-acao-${i}`}>
              {ACOES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {o.acao === 'setor' && (
              <select value={o.setor_id || ''} onChange={e => muda(m => { m.opcoes[i].setor_id = e.target.value; })} className="w-full h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" data-testid={`wa-ed-op-setor-${i}`}>
                <option value="">Escolha o setor…</option>{setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            )}
            {o.acao === 'submenu' && (
              fluxo.menus[o.menu_id] && o.menu_id !== 'inicio'
                ? <button type="button" onClick={() => setSel({ tipo: 'menu', id: o.menu_id })} className="text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1 hover:underline"><CornerDownRight className="w-3.5 h-3.5" /> Editar o submenu “{fluxo.menus[o.menu_id].titulo}”</button>
                : (
                  <div className="flex gap-1.5">
                    <select value="" onChange={e => e.target.value && muda(m => { m.opcoes[i].menu_id = e.target.value; })} className="flex-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm">
                      <option value="">Usar um submenu existente…</option>
                      {Object.values(fluxo.menus).filter(m => m.id !== 'inicio' && m.id !== id).map(m => <option key={m.id} value={m.id}>{m.titulo}</option>)}
                    </select>
                    <button type="button" onClick={() => novoSubmenu(o)} className="h-8 px-2.5 rounded-md bg-emerald-600 text-white text-xs font-medium" data-testid={`wa-ed-op-novosub-${i}`}>Criar novo</button>
                  </div>
                )
            )}
            {o.acao === 'coleta' && (
              fluxo.coletas?.[o.coleta_id]
                ? <button type="button" onClick={() => setSel({ tipo: 'coleta', id: o.coleta_id })} className="text-xs text-violet-700 dark:text-violet-300 flex items-center gap-1 hover:underline"><CornerDownRight className="w-3.5 h-3.5" /> Editar a coleta “{fluxo.coletas[o.coleta_id].titulo}”</button>
                : (
                  <div className="flex gap-1.5">
                    <select value="" onChange={e => e.target.value && muda(m => { m.opcoes[i].coleta_id = e.target.value; })} className="flex-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm">
                      <option value="">Usar uma coleta existente…</option>
                      {Object.values(fluxo.coletas || {}).map(c => <option key={c.id} value={c.id}>{c.titulo}</option>)}
                    </select>
                    <button type="button" onClick={() => novaColeta(o)} className="h-8 px-2.5 rounded-md bg-violet-600 text-white text-xs font-medium" data-testid={`wa-ed-op-novacoleta-${i}`}>Criar nova</button>
                  </div>
                )
            )}
            {o.acao === 'mensagem' && (
              <>
                <textarea rows={3} value={o.texto || ''} onChange={e => muda(m => { m.opcoes[i].texto = e.target.value; })} placeholder="Ex.: Atendemos de segunda a sexta, das 8h às 18h."
                  className="w-full px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" data-testid={`wa-ed-op-texto-${i}`} />
                <select value={o.depois || 'menu'} onChange={e => muda(m => { m.opcoes[i].depois = e.target.value; })} className="w-full h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm">
                  <option value="menu">Depois, mostra o menu de novo</option>
                  <option value="atendente">Depois, passa para um atendente</option>
                  <option value="fim">Depois, só aguarda o cliente</option>
                </select>
              </>
            )}
            <select value={o.visivel || 'todos'} onChange={e => muda(m => { m.opcoes[i].visivel = e.target.value; })} className="w-full h-7 px-2 rounded-md bg-slate-50 dark:bg-slate-800 text-xs border-0">
              <option value="todos">Aparece para todos</option>
              <option value="clientes">Só para quem já é cliente</option>
              <option value="nao_clientes">Só para quem ainda não é cliente</option>
            </select>
          </div>
        ))}
        {(menu.opcoes || []).length < 9 && (
          <button type="button" onClick={() => { const nid = novoId('op'); muda(m => { m.opcoes.push({ id: nid, rotulo: '', acao: 'atendente', visivel: 'todos' }); }); setSel({ tipo: 'menu', id, op: nid }); }}
            className="w-full h-9 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:border-emerald-400 flex items-center justify-center gap-1.5" data-testid="wa-ed-op-nova">
            <Plus className="w-4 h-4" /> Adicionar opção
          </button>
        )}
      </div>
    </div>
  );
}

function EditorColeta({ fluxo, setFluxo, id, setores, setSel }) {
  const c = fluxo.coletas[id];
  const muda = (fn) => setFluxo(f => fn(f.coletas[id], f));
  const usado = Object.values(fluxo.menus).some(m => m.opcoes?.some(o => o.acao === 'coleta' && o.coleta_id === id));
  return (
    <div className="space-y-3" data-testid={`wa-ed-coleta-${id}`}>
      <div className="flex items-center gap-2">
        <h4 className="font-semibold text-sm flex-1">Coleta de dados</h4>
        <button type="button" disabled={usado} title={usado ? 'Tire as opções que usam esta coleta antes' : 'Excluir coleta'}
          onClick={() => { setFluxo(f => { delete f.coletas[id]; }); setSel({ tipo: 'menu', id: 'inicio' }); }}
          className="h-7 px-2 rounded-md text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-40 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
      </div>
      <label className="block text-sm font-medium">Nome (aparece na nota para o atendente)
        <input value={c.titulo || ''} onChange={e => muda(x => { x.titulo = e.target.value; })} className={inputCls} />
      </label>
      <label className="block text-sm font-medium">Mensagem de abertura <span className="text-xs font-normal text-slate-500">(opcional)</span>
        <textarea rows={2} value={c.intro || ''} onChange={e => muda(x => { x.intro = e.target.value; })} className={areaCls} />
      </label>
      <div className="space-y-2">
        <p className="text-sm font-medium">Perguntas <span className="text-xs font-normal text-slate-500">(uma de cada vez; o cliente pode digitar “cancelar”)</span></p>
        {(c.perguntas || []).map((p, i) => (
          <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 space-y-1.5" data-testid={`wa-ed-perg-${i}`}>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-violet-700 dark:text-violet-300 w-5">{i + 1}.</span>
              <input value={p.pergunta} onChange={e => muda(x => { x.perguntas[i].pergunta = e.target.value; })} placeholder="Ex.: Quantos hectares tem a propriedade?"
                className="flex-1 min-w-0 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" data-testid={`wa-ed-perg-texto-${i}`} />
              <button type="button" disabled={i === 0} onClick={() => muda(x => { [x.perguntas[i - 1], x.perguntas[i]] = [x.perguntas[i], x.perguntas[i - 1]]; })} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" aria-label="Subir"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" disabled={c.perguntas.length <= 1} onClick={() => muda(x => { x.perguntas.splice(i, 1); })} className="p-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-30" aria-label="Remover"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <select value={p.tipo || 'texto'} onChange={e => muda(x => { x.perguntas[i].tipo = e.target.value; })} className="w-full h-7 px-2 rounded-md bg-slate-50 dark:bg-slate-800 text-xs border-0">
              {TIPOS.map(([k, l]) => <option key={k} value={k}>Resposta: {l}</option>)}
            </select>
          </div>
        ))}
        {(c.perguntas || []).length < 10 && (
          <button type="button" onClick={() => muda(x => { x.perguntas.push({ campo: '', pergunta: '', tipo: 'texto' }); })}
            className="w-full h-9 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:border-violet-400 flex items-center justify-center gap-1.5" data-testid="wa-ed-perg-nova">
            <Plus className="w-4 h-4" /> Adicionar pergunta
          </button>
        )}
      </div>
      <label className="block text-sm font-medium">Mensagem no final <span className="text-xs font-normal text-slate-500">(opcional)</span>
        <textarea rows={2} value={c.final_texto || ''} onChange={e => muda(x => { x.final_texto = e.target.value; })} className={areaCls} />
      </label>
      <label className="block text-sm font-medium">Depois das respostas
        <select value={c.destino?.acao || 'fila'} onChange={e => muda(x => { x.destino = { acao: e.target.value, setor_id: x.destino?.setor_id }; })} className={inputCls} data-testid="wa-ed-coleta-destino">
          <option value="fila">Passa para um atendente</option>
          <option value="setor">Vai para a fila de um setor</option>
          <option value="menu">Volta ao menu principal</option>
          <option value="fim">Encerra o assistente (aguarda o cliente)</option>
        </select>
      </label>
      {c.destino?.acao === 'setor' && (
        <select value={c.destino.setor_id || ''} onChange={e => muda(x => { x.destino.setor_id = e.target.value; })} className={inputCls}>
          <option value="">Escolha o setor…</option>{setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
      )}
      <p className="text-xs text-slate-500">As respostas ficam numa nota para o atendente e na ficha do contato.</p>
    </div>
  );
}

/* ============================== simulador ============================== */

const valida = (tipo, t) => {
  const d = t.replace(/\D/g, '');
  if (tipo === 'numero') return /^\d+([.,]\d+)*$/.test(t.replace(/\s/g, '')) || 'Digite só o número, por favor.';
  if (tipo === 'email') return /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(t) || 'Esse e-mail não parece válido. Pode conferir?';
  if (tipo === 'telefone') return (d.length >= 10 && d.length <= 13) || 'Digite o telefone com DDD, por favor.';
  if (tipo === 'cpf') return [11, 14].includes(d.length) || 'Esse CPF/CNPJ não parece válido. Confira e digite de novo.';
  if (tipo === 'data') return /^\d{1,2}[/\-. ]\d{1,2}[/\-. ]\d{2,4}$/.test(t.trim()) || 'Digite a data no formato *dia/mês/ano*, ex.: 25/03/1980.';
  return t.trim().length >= 2 || 'Pode responder com um pouco mais de detalhe?';
};

function Simulador({ fluxo, empresa, assistente, setorDe }) {
  const [cliente, setCliente] = useState(false);
  const [estado, setEstado] = useState({ etapa: null, menu: 'inicio', coleta: null, i: 0 });
  const [log, setLog] = useState([]);
  const [txt, setTxt] = useState('');
  const fimRef = useRef(null);
  useEffect(() => { fimRef.current?.scrollIntoView({ block: 'end' }); }, [log]);
  const reiniciar = () => { setEstado({ etapa: null, menu: 'inicio', coleta: null, i: 0 }); setLog([]); };
  useEffect(reiniciar, [fluxo, cliente]); // eslint-disable-line react-hooks/exhaustive-deps

  const fila = (setor) => `✅ Pronto! Você entrou ${setor ? `na fila do setor *${setor.nome}*` : 'na fila de atendimento'}.\n📄 Protocolo: *#2026-000123*\n\nEm instantes um de nossos atendentes vai falar com você por aqui.`;
  const responder = (entrada) => {
    const t = entrada.trim(); if (!t) return;
    const tn = norm(t);
    const saida = [];
    let e = { ...estado };
    const bot = (s) => saida.push({ de: 'bot', texto: s });
    const executar = (o) => {
      if (o.acao === 'submenu') { e = { ...e, etapa: 'menu', menu: o.menu_id }; bot(textoMenu(fluxo, o.menu_id, cliente)); }
      else if (o.acao === 'mensagem') { bot(o.texto + (o.depois === 'menu' || !o.depois ? `\n\n${textoMenu(fluxo, e.menu, cliente)}` : '')); if (o.depois === 'atendente') { bot(fila(null)); e = { ...e, etapa: 'fim' }; } }
      else if (o.acao === 'setor') { bot(fila(setorDe(o.setor_id))); e = { ...e, etapa: 'fim' }; }
      else if (o.acao === 'atendente') { bot(fila(null)); e = { ...e, etapa: 'fim' }; }
      else if (o.acao === 'consulta_projeto') { bot('Por favor, digite seu *CPF* (só os números).'); e = { ...e, etapa: 'cpf' }; }
      else if (o.acao === 'cadastro') { bot('📝 Vamos fazer seu cadastro! São só 5 perguntas rápidas.\n\n_(no teste o cadastro para aqui)_'); e = { ...e, etapa: 'fim' }; }
      else if (o.acao === 'coleta') {
        const c = fluxo.coletas?.[o.coleta_id]; if (!c) return;
        e = { ...e, etapa: 'coleta', coleta: c.id, i: 0 };
        bot(`${c.intro ? `${c.intro}\n(Digite *cancelar* para sair.)\n\n` : ''}*1/${c.perguntas.length}* – ${c.perguntas[0]?.pergunta}`);
      }
    };
    saida.push({ de: 'cliente', texto: t });
    if (e.etapa === 'fim') bot('_(conversa com um atendente: o assistente não responde mais)_');
    else if (['menu', 'inicio', 'voltar', '0'].includes(tn) && e.etapa !== 'coleta') { e = { ...e, etapa: 'menu', menu: 'inicio' }; bot(textoMenu(fluxo, 'inicio', cliente)); }
    else if (['atendente', 'humano', 'pessoa'].includes(tn) && e.etapa !== 'coleta') { bot(fila(null)); e = { ...e, etapa: 'fim' }; }
    else if (e.etapa === 'coleta') {
      const c = fluxo.coletas?.[e.coleta];
      if (['cancelar', 'sair'].includes(tn)) { e = { ...e, etapa: 'menu', menu: 'inicio' }; bot(`Tudo bem, cancelei. 🙂\n\n${textoMenu(fluxo, 'inicio', cliente)}`); }
      else {
        const p = c.perguntas[e.i]; const ok = valida(p.tipo, t);
        if (ok !== true) bot(ok);
        else if (e.i + 1 < c.perguntas.length) { e = { ...e, i: e.i + 1 }; bot(`*${e.i + 1}/${c.perguntas.length}* – ${c.perguntas[e.i].pergunta}`); }
        else {
          if (c.final_texto) bot(c.final_texto);
          const d = c.destino || {};
          if (d.acao === 'setor') { bot(fila(setorDe(d.setor_id))); e = { ...e, etapa: 'fim' }; }
          else if (d.acao === 'menu') { e = { ...e, etapa: 'menu', menu: 'inicio' }; bot(textoMenu(fluxo, 'inicio', cliente)); }
          else if (d.acao === 'fim') e = { ...e, etapa: 'menu', menu: 'inicio' };
          else { bot(fila(null)); e = { ...e, etapa: 'fim' }; }
          saida.push({ de: 'nota', texto: `${c.titulo}: respostas guardadas para o atendente` });
        }
      }
    } else if (e.etapa === 'cpf') { bot('_(aqui o assistente confere o CPF e mostra a etapa de cada projeto)_'); e = { ...e, etapa: 'menu' }; }
    else if (!e.etapa) {
      const menu = fluxo.menus.inicio; const ops = visiveis(menu, cliente);
      const o = (/^\d+$/.test(tn) && ops[Number(tn) - 1]) || ops.find(x => norm(x.rotulo) === tn);
      e = { ...e, etapa: 'menu', menu: 'inicio' };
      if (o) executar(o); else bot(`${saudacaoTexto(fluxo, cliente ? 'João' : '', empresa, assistente)}\n\n${textoMenu(fluxo, 'inicio', cliente)}`);
    } else {
      const ops = visiveis(fluxo.menus[e.menu] || fluxo.menus.inicio, cliente);
      const o = (/^\d+$/.test(tn) && ops[Number(tn) - 1]) || ops.find(x => norm(x.rotulo) === tn);
      if (o) executar(o); else bot(`Desculpe, não entendi. 🤔\n\n${textoMenu(fluxo, e.menu, cliente)}`);
    }
    setEstado(e); setLog(l => [...l, ...saida]); setTxt('');
  };
  const sugestoes = estado.etapa === 'menu' || !estado.etapa
    ? (estado.etapa ? visiveis(fluxo.menus[estado.menu] || fluxo.menus.inicio, cliente).map((o, i) => String(i + 1)).concat(estado.menu !== 'inicio' ? ['0'] : []) : ['oi'])
    : estado.etapa === 'coleta' ? ['cancelar'] : [];

  return (
    <div className="flex flex-col h-[560px]" data-testid="wa-simulador">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={cliente} onChange={e => setCliente(e.target.checked)} className="w-3.5 h-3.5 accent-emerald-600" /> Testar como cliente cadastrado</label>
        <span className="flex-1" />
        <button onClick={reiniciar} className="flex items-center gap-1 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><RotateCcw className="w-3.5 h-3.5" /> Recomeçar</button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#f1f3ee] dark:bg-slate-950">
        {!log.length && <p className="text-xs text-center text-slate-500 py-6">Escreva como se fosse o cliente (ex.: “oi”). Nada é enviado de verdade.</p>}
        {log.map((m, i) => (
          m.de === 'nota'
            ? <p key={i} className="text-[11px] text-center text-slate-500">— {m.texto} —</p>
            : (
              <div key={i} className={`max-w-[88%] rounded-lg px-2.5 py-1.5 text-[13px] whitespace-pre-wrap shadow-sm ${m.de === 'cliente' ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100' : 'ml-auto bg-[#dcf5e1] dark:bg-emerald-900/70 text-slate-900 dark:text-slate-100'}`}
                data-testid={m.de === 'bot' ? 'wa-sim-bot' : 'wa-sim-cliente'}>
                {formatWa(m.texto)}
              </div>
            )
        ))}
        <div ref={fimRef} />
      </div>
      <div className="p-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
        {sugestoes.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {sugestoes.map(s => <button key={s} onClick={() => responder(s)} className="h-7 min-w-7 px-2 rounded-full border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`wa-sim-sug-${s}`}>{s}</button>)}
          </div>
        )}
        <form onSubmit={e => { e.preventDefault(); responder(txt); }} className="flex gap-1.5">
          <input value={txt} onChange={e => setTxt(e.target.value)} placeholder="Mensagem do cliente" className="flex-1 h-9 px-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none" data-testid="wa-sim-input" />
          <button className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center" aria-label="Enviar"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    </div>
  );
}
